/**
 * property-time.ts — client-side helper for property-local check-in/out instants.
 *
 * Mirrors server/lib/property-time.ts so countdowns on the Guests page agree
 * with the server's dashboard classification.
 */

export const CHECKIN_HOUR = 15;
export const CHECKOUT_HOUR = 11;

const LOCATION_TO_TZ: Record<string, string> = {
  switzerland: 'Europe/Zurich',
  dubai: 'Asia/Dubai',
  thailand: 'Asia/Bangkok',
};

export interface Property {
  id: number;
  name: string;
  location?: string;
  timezone?: string;
}

export function timezoneForLocation(location: string | null | undefined): string {
  if (!location) return 'UTC';
  return LOCATION_TO_TZ[location.toLowerCase()] || 'UTC';
}

export function getPropertyTimezone(propertyName: string, properties: Property[]): string {
  const p = properties.find(
    (prop) => prop.name.trim().toLowerCase() === propertyName.trim().toLowerCase(),
  );
  return p?.timezone || timezoneForLocation(p?.location);
}

function parseDateParts(parts: Intl.DateTimeFormatPart[]) {
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    h: get('hour'),
    min: get('minute'),
    s: get('second'),
  };
}

function dateFromParts(p: { y: number; m: number; d: number; h: number; min: number; s: number }) {
  return new Date(Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s));
}

export function toInstantInZone(
  dateStr: string | null | undefined,
  timeZone: string,
  hour: number,
  minute: number = 0,
): Date | null {
  if (!dateStr) return null;
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [_, y, mo, d] = m.map(Number);

  const targetMs = Date.UTC(y, mo - 1, d, hour, minute);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });

  let U = targetMs;
  for (let i = 0; i < 4; i++) {
    const p = parseDateParts(fmt.formatToParts(new Date(U)));
    const localAsUtc = dateFromParts(p).getTime();
    const offset = U - localAsUtc;
    const nextU = targetMs + offset;
    if (Math.abs(nextU - U) < 1000) return new Date(nextU);
    U = nextU;
  }
  return new Date(U);
}

export function toCheckinInstant(dateStr: string | null | undefined, timeZone: string): Date | null {
  return toInstantInZone(dateStr, timeZone, CHECKIN_HOUR);
}

export function toCheckoutInstant(dateStr: string | null | undefined, timeZone: string): Date | null {
  return toInstantInZone(dateStr, timeZone, CHECKOUT_HOUR);
}
